import { useEffect, useState } from 'react';
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  Box,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import axiosInstance from '../../../utils/axios';
import { getChecklist } from '../../../atoms/setup/SetupAtoms';
import { useAtom } from 'jotai';
import { isReadOnly } from '../../../utils/permissionUtils';

export default function FormComponent(props) {
  const { currentProject, data, value, loader } = props;
  const [, setCheckLists] = useAtom(getChecklist);

  const [activities, setActivities] = useState([
    {
      activity_id: null, // No ID for new activities
      activity: '',
      part_id: '', // Will be set when data loads
      weight: 0,
      sub_activity: [
        { 
          activity_id: null, // No ID for new sub-activities
          activity: '', 
          part_id: '', 
          weight: null 
        },
      ],
    },
  ]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Calculate total weight from all parent activities
  const totalActivityWeight = activities
    .filter((a) => !a.delete)
    .reduce((sum, a) => sum + (a.weight || 0), 0);

  // Calculate parent activity weight based on sub-activities
  const calculateParentWeight = (activity) => {
    return activity.sub_activity
      .filter((s) => !s.delete)
      .reduce((sum, s) => sum + (s.weight || 0), 0);
  };

  // Update parent weight when sub-activities change (removed - no longer needed)

  const isFormValid = () => {
    const allSubWeightsMatch = activities
      .filter((a) => !a.delete)
      .every((a) => {
        const subTotal = calculateParentWeight(a);
        return subTotal === a.weight;
      });
    return totalActivityWeight === 100 && allSubWeightsMatch;
  };

  const handleSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const addActivity = () => {
    const newActivity = {
      activity_id: null, // No ID for new activities
      activity: '',
      part_id: data?.[0]?.id || '', // Set first part ID by default
      weight: 0,
      sub_activity: [
        {
          activity_id: null, // No ID for new sub-activities
          activity: '',
          part_id: '',
          weight: null,
        },
      ],
    };
    setActivities([...activities, newActivity]);
  };

  const addSubActivity = (activityIndex) => {
    setActivities(
      activities.map((activity, index) =>
        index === activityIndex
          ? {
              ...activity,
              sub_activity: [
                ...activity.sub_activity,
                {
                  activity_id: null, // No ID for new sub-activities
                  activity: '',
                  part_id: '',
                  weight: null,
                },
              ],
            }
          : activity,
      ),
    );
  };

  const deleteActivity = (activityIndex) => {
    setActivities(prevActivities => {
      const updatedActivities = [...prevActivities];
      // If the activity has an ID (existing activity), mark as deleted
      if (updatedActivities[activityIndex].activity_id) {
        updatedActivities[activityIndex] = { 
          ...updatedActivities[activityIndex], 
          delete: true 
        };
      } else {
        // If no ID (new activity), remove from array completely
        updatedActivities.splice(activityIndex, 1);
      }
      return updatedActivities;
    });
  };

  const deleteSubActivity = (activityIndex, subActivityIndex) => {
    setActivities(prevActivities => {
      const updatedActivities = [...prevActivities];
      const activity = updatedActivities[activityIndex];
      
      // If the sub-activity has an ID (existing), mark as deleted
      if (activity.sub_activity[subActivityIndex].activity_id) {
        activity.sub_activity[subActivityIndex] = {
          ...activity.sub_activity[subActivityIndex],
          delete: true
        };
      } else {
        // If no ID (new sub-activity), remove from array completely
        activity.sub_activity.splice(subActivityIndex, 1);
      }
      
      updatedActivities[activityIndex] = activity;
      return updatedActivities;
    });
  };

  // Handle sub-activity weight changes with immediate parent weight calculation
  const handleSubActivityWeightChange = (activityIndex, subActivityIndex, newWeight) => {
    const weight = newWeight === '' ? null : Number(newWeight);
    
    setActivities(prevActivities =>
      prevActivities.map((activity, aIndex) => {
        if (aIndex === activityIndex) {
          const updatedActivity = {
            ...activity,
            sub_activity: activity.sub_activity.map((subActivity, sIndex) =>
              sIndex === subActivityIndex ? { ...subActivity, weight } : subActivity
            ),
          };
          
          // Calculate and update parent weight immediately in the same state update
          const calculatedParentWeight = updatedActivity.sub_activity
            .filter((s) => !s.delete)
            .reduce((sum, s) => sum + (s.weight || 0), 0);
          
          return { ...updatedActivity, weight: calculatedParentWeight };
        }
        return activity;
      })
    );
  };

  // Handle activity field changes
  const handleActivityChange = (activityIndex, field, value) => {
    setActivities(
      activities.map((activity, index) =>
        index === activityIndex ? { ...activity, [field]: value } : activity
      )
    );
  };

  // Handle sub-activity field changes
  const handleSubActivityChange = (activityIndex, subActivityIndex, field, value) => {
    setActivities(
      activities.map((activity, aIndex) =>
        aIndex === activityIndex
          ? {
              ...activity,
              sub_activity: activity.sub_activity.map((subActivity, sIndex) =>
                sIndex === subActivityIndex ? { ...subActivity, [field]: value } : subActivity
              ),
            }
          : activity,
      ),
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      handleSnackbar(
        'All activity weights must total 100%, and sub-activities must match parent weight.',
        'error',
      );
      return;
    }

    try {
      setIsLoading(true);

      const result = activities.map((parent) => {
        const parentActivity = {
          activity: parent.activity,
          part_id: parent.part_id,
          weight: parent.weight,
          subShow: !!parent.sub_activity?.length,
          weight_decimal: parent.weight,
          sub_activity: parent.sub_activity?.map((child) => {
            const childActivity = {
              activity: child.activity,
              part_id: child.part_id,
              weight: child.weight,
              weight_decimal: child.weight,
            };
            
            // Only include activity_id if it exists (for existing sub-activities)
            if (child.activity_id) {
              childActivity.activity_id = child.activity_id;
            }
            
            return childActivity;
          }) || [],
        };
        
        // Only include activity_id if it exists (for existing activities)
        if (parent.activity_id) {
          parentActivity.activity_id = parent.activity_id;
        }
        
        return parentActivity;
      });

      const finalData = {
        data: result,
        checklist_type: "Construction",
        checklist_id: value?.checklist_id ? value?.checklist_id : null,
      }

      const response = await axiosInstance.post(`checklist/create_construction_checklist`, finalData);
      if (response?.data) {
        handleSnackbar('Construction Checklist saved successfully');
        setCheckLists("Construction")
      }
    } catch (err) {
      handleSnackbar(
        'Something went wrong',
        'error',
      );
    } finally {
      setIsEditing(false);
      setIsLoading(false);
    }
  };

  // Initialize activities when editing existing data
  useEffect(() => {
    if (value?.checklist_id && value?.data) {
      setActivities(value.data);
    }
  }, [value]);

  // Set default part_id when data loads (for new activities and initial state)
  useEffect(() => {
    if (data?.[0]?.id) {
      setActivities(prevActivities =>
        prevActivities.map(activity => ({
          ...activity,
          // Only set part_id if it's empty (for new activities)
          part_id: activity.part_id || data[0].id
        }))
      );
    }
  }, [data]);

  return (
    <Box>
      <Box px={4} py={2}>
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography fontSize={22} fontWeight={600}>
            {currentProject?.name} - CP Checklist
          </Typography>
          <Box display="flex" alignItems="center" gap={4}>
            <Typography color={isFormValid() ? 'green' : 'error'}>
              Weight: {totalActivityWeight}%
            </Typography>
            {isEditing && (
              <IconButton
                color="primary"
                sx={{
                  color: 'white',
                  padding: '4px',
                  background: '#f16253',
                  '&:hover': {
                    color: 'black',
                    background: 'gray',
                  },
                }}
                onClick={addActivity}
              >
                <Add />
              </IconButton>
            )}
            {!isEditing ? (
              <Button variant="contained" onClick={() => setIsEditing(true)} disabled ={isReadOnly()}>
                Edit
              </Button>
            ) : (
              <Box sx={{ display: "flex", gap: 4 }}>
                <Button
                  variant="contained"
                  disabled={!isFormValid() || isLoading}
                  onClick={handleSubmit}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Save'}
                </Button>
                <Button variant="outlined" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {loader ? (
          <Box
            sx={{
              height: "calc(100vh - 208px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box py={1} sx={{ overflowY: 'auto', overflowX: 'hidden', height: 'calc(100vh - 204px)', backgroundColor: !isEditing ? "#f3f4f6" : "white" }}>
            {activities
              .filter((activity) => !activity.delete)
              .map((activity, activityIndex) => {
                const subTotalWeight = calculateParentWeight(activity);
                const mismatch = subTotalWeight !== activity.weight;
                const displayIndex = activities
                  .slice(0, activities.indexOf(activity))
                  .filter(a => !a.delete).length + 1;

                return (
                  <Box px={2} key={`activity-${activityIndex}`} mb={2}>
                    <Box px={2} gap={4} display="flex" alignItems="center">
                      <Grid item xs={12} sm={1} mt={4}>
                        <Typography variant="body2">{displayIndex}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={3} width="100%">
                        <Typography variant="body2" fontWeight="600" fontSize={18} mb={1}>
                          Activity Name
                        </Typography>
                        <TextField
                          fullWidth
                          label="Activity Name"
                          value={activity.activity}
                          disabled={!isEditing}
                          onChange={(e) =>
                            handleActivityChange(activityIndex, 'activity', e.target.value)
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={3} width="100%">
                        <Typography variant="body2" fontWeight="600" fontSize={18} mb={1}>
                          Unit of Count
                        </Typography>
                        <Select
                          fullWidth
                          sx={{ height: '52px' }}
                          value={activity.part_id}
                          disabled={true} // Always disabled for parent activities
                          displayEmpty
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 300, overflowY: 'auto' },
                            },
                          }}
                        >
                          <MenuItem value="">Select Unit</MenuItem>
                          {data?.map((item) => (
                            <MenuItem key={item?.id} value={item?.id}>
                              {item?.part_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>

                      <Grid item xs={12} sm={3} width="100%">
                        <Typography variant="body2" fontWeight="600" fontSize={18} mb={1}>
                          Weight
                        </Typography>
                        <TextField
                          fullWidth
                          type="number"
                          label="Weight"
                          value={activity.weight || 0}
                          disabled={true} // Always disabled as it's auto-calculated
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>

                      <Grid item sm={2} mt={4}>
                        {isEditing && (
                          <IconButton onClick={() => deleteActivity(activityIndex)} color="error">
                            <Delete />
                          </IconButton>
                        )}
                      </Grid>
                    </Box>

                    {mismatch && activity.weight && (
                      <Typography color="error" ml={12} mt={1}>
                        Sub-activity total ({subTotalWeight}%) must equal activity weight ({activity.weight}%)
                      </Typography>
                    )}

                    <Box ml={4} mt={2}>
                      {activity.sub_activity
                        .filter((s) => !s.delete)
                        .map((sub, subActivityIndex) => {
                          const subDisplayIndex = activity.sub_activity
                            .slice(0, activity.sub_activity.indexOf(sub))
                            .filter(s => !s.delete).length + 1;

                          return (
                            <Box
                              px={2}
                              key={`sub-activity-${activityIndex}-${subActivityIndex}`}
                              display="flex"
                              gap={4}
                              alignItems="center"
                              mt={2}
                            >
                              <Grid item xs={12} sm={1}>
                                <Typography variant="body2">{`${displayIndex}.${subDisplayIndex}`}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={3} width="100%">
                                <TextField
                                  label="Sub-activity Name"
                                  value={sub.activity}
                                  fullWidth
                                  disabled={!isEditing}
                                  onChange={(e) =>
                                    handleSubActivityChange(activityIndex, subActivityIndex, 'activity', e.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={3} width="100%">
                                <Select
                                  fullWidth
                                  sx={{ height: '52px' }}
                                  value={sub.part_id}
                                  displayEmpty
                                  disabled={!isEditing}
                                  MenuProps={{
                                    PaperProps: {
                                      style: { maxHeight: 300, overflowY: 'auto' },
                                    },
                                  }}
                                  onChange={(e) =>
                                    handleSubActivityChange(activityIndex, subActivityIndex, 'part_id', e.target.value)
                                  }
                                >
                                  <MenuItem value="">Select Unit</MenuItem>
                                  {data?.map((item) => (
                                    <MenuItem key={item?.id} value={item?.id}>
                                      {item?.part_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Grid>
                              <Grid item xs={12} sm={3} width="100%">
                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Weight"
                                  value={sub.weight || ''}
                                  disabled={!isEditing}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                  }}
                                  onChange={(e) =>
                                    handleSubActivityWeightChange(activityIndex, subActivityIndex, e.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item sm={2}>
                                {isEditing && (
                                  <IconButton onClick={() => deleteSubActivity(activityIndex, subActivityIndex)} color="error">
                                    <Delete />
                                  </IconButton>
                                )}
                              </Grid>
                            </Box>
                          );
                        })}
                      {isEditing && (
                        <Box
                          mt={2}
                          sx={{ display: 'flex', justifyContent: 'flex-end', marginRight: '4.5%' }}
                        >
                          <Button
                            variant="outlined"
                            onClick={() => addSubActivity(activityIndex)}
                            startIcon={<Add />}
                          >
                            Add Sub-activity
                          </Button>
                        </Box>
                      )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </Box>
                );
              })}
          </Box>
        )}
      </Box>
    </Box>
  );
}